import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { SessionService } from 'src/app/store/session/session.service';
import {
  InterceptorSkipDealerIdHeader,
  InterceptorSkipHeader,
  InterceptorSkipOemDealerIdHeader,
} from '../guards/skip.interceptor';
import { AmplifyService } from './amplify.service';
import { environment } from '@env/environment';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { NewRelicService } from 'src/config/newrelic.service';

interface HttpErrorHandlerOptions {
  title: string;
  statusFilter?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private amplifyService: AmplifyService,
    private sessionService: SessionService,
    private router: Router,
    private toastr: ToastrService,
    private newRelicService: NewRelicService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes(environment.superBidRefreshTokenUrl)) {
      return next.handle(request);
    }

    return from(this.amplifyService.isAuthenticated()).pipe(
      mergeMap(isAuthenticate => {
        if (isAuthenticate) {
          return this.amplifyService.getJwt().pipe(
            mergeMap(jwt => {
              const oemDealerSelected = this.sessionService.getOemDealerSelected();
              const dealerSelected = this.sessionService.getDealerSelected();
              if (oemDealerSelected && oemDealerSelected.id && dealerSelected && dealerSelected.id) {
                return this.setJwtOrSkip(request, next, jwt, oemDealerSelected.id, dealerSelected.id);
              }

              if (dealerSelected && dealerSelected.id) {
                return this.setJwtOrSkip(request, next, jwt, null, dealerSelected.id);
              }

              if (oemDealerSelected && oemDealerSelected.id) {
                return this.setJwtOrSkip(request, next, jwt, oemDealerSelected.id, null);
              }
              return this.setJwtOrSkip(request, next, jwt, null, null);
            }),
            catchError(err => {
              if (err.status === 0) {
                this.handleHttpError(err, request, { title: 'Atenção' });
                return throwError(() => err);
              }

              this.handleHttpError(err, request, {
                title: 'Atenção',
                statusFilter: [400, 401, 403, 404, 500],
              });

              return throwError(() => err);
            })
          );
        } else if (this.sessionService.forcedJwt) {
          return this.setJwtOrSkip(request, next, this.sessionService.forcedJwt, null, null);
        } else if (this.hasLocalStorageToken()) {
          return this.getLocalStorageToken().pipe(
            mergeMap(token => this.setOemDealerIdHeader(request, next, token))
          );

        }
        let headers = request.headers.delete(InterceptorSkipHeader);
        headers = headers.delete(InterceptorSkipOemDealerIdHeader);
        headers = headers.delete(InterceptorSkipDealerIdHeader);
        request = request.clone({ headers });
        return next.handle(request).pipe(
          catchError(err => {
            this.handleHttpError(err, request, { title: 'Erro' });
            return throwError(() => err);
          })
        );
      })
    );
  }

  private handleHttpError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    options: HttpErrorHandlerOptions,
  ): void {
    if (options.statusFilter && !options.statusFilter.includes(err.status)) {
      return;
    }

    if (err.error?.errorCode === 'must-assume-pricing') {
      return;
    }

    const is403 = err.status === 403 && err.error?.errorCode;
    const shouldShowError = is403 || err.status !== 403;

    if (!shouldShowError) {
      return;
    }

    const isGenericSupportError = !err.error?.errorCode;
    const message = err.status === 0
      ? 'Erro de conexão. Verifique sua internet e tente novamente.'
      : isGenericSupportError
        ? 'Contate o suporte para mais informações'
        : err.error.message;

    this.toastr.error(message || 'Ocorreu um erro', options.title);

    this.newRelicService.reportApiError({
      endpointUrl: request.url,
      httpMethod: request.method,
      httpStatus: err.status,
      body: err.error,
      errorMessage: err.message,
      statusText: err.statusText,
      pageOrigin: window.location.origin,
      pageHostname: window.location.hostname,
      pageHref: window.location.href,
      navigatorOnline: navigator.onLine,
      errorEventType: err.error?.type ?? '',
      isAborted: err.message?.toLowerCase().includes('abort') ?? false,
    });

    if (err.error?.errorCode === 'migration-required') {
      this.router.navigate(['/logout']);
    }
  }

  private hasLocalStorageToken(): boolean {
    return localStorage.getItem('accessToken') !== null;
  }

  private getLocalStorageToken(): Observable<string> {
    const expiresAt = Number.parseInt(localStorage.getItem('expiresAt'));
    const now = new Date().getTime();
    if (expiresAt < now) {
      return this.sessionService.refreshToken();
    } else {
      return of(localStorage.getItem('accessToken'));
    }
  }


  private setOemDealerIdHeader(request: HttpRequest<any>, next: HttpHandler, jwt: string): Observable<HttpEvent<any>> {
    const oemDealerSelected = this.sessionService.getOemDealerSelected();
    if (oemDealerSelected && oemDealerSelected.id) {
      return this.setJwtOrSkip(request, next, jwt, oemDealerSelected.id, null);
    } else {
      return this.setJwtOrSkip(request, next, jwt, null, null);
    }
  }

  private setJwtOrSkip(
    request: HttpRequest<any>,
    next: HttpHandler,
    jwt: string,
    oemDealerId: string,
    dealerId: string
  ): Observable<HttpEvent<any>> {
    let headers = request.headers;

    if (!request.headers.has(InterceptorSkipHeader)) {
      headers = headers.append('Authorization', `Bearer ${jwt}`);
    }

    if (!request.headers.has(InterceptorSkipOemDealerIdHeader) && oemDealerId) {
      headers = headers.append('X-OemDealerId', oemDealerId);
    }

    if (!request.headers.has(InterceptorSkipDealerIdHeader) && dealerId) {
      headers = headers.append('X-dealerId', dealerId);
    }

    headers = headers.delete(InterceptorSkipHeader);
    headers = headers.delete(InterceptorSkipOemDealerIdHeader);
    headers = headers.delete(InterceptorSkipDealerIdHeader);
    request = request.clone({ headers });

    return next.handle(request);
  }
}
